import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState, type CSSProperties } from 'react';
import styled, { keyframes } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useTransitionApi } from '@sorare/core/src/hooks/ui/useTransitionApi.tsx';
import { absCenter } from '@sorare/core/src/lib/style';

import { Tile } from '#src/components/series/SeriesBoard/HotStreakTile/Tile/index.tsx';
import { DiamondBaseColor } from '#src/components/series/SeriesBoard/HotStreakTile/Tile/utils.ts';
import {
  DIAMOND_HEIGHT,
  DIAMOND_WIDTH,
} from '#src/components/series/SeriesBoard/utils.ts';

import {
  NewChapterOverlay,
  VIEW_TRANSITION_NAME,
} from '../NewChapterOverlay/index.tsx';
import { useMaybeClaimInviteTask } from '../useMaybeClaimInviteTask.ts';
import { RefereeRewardDialog } from './RefereeRewardDialog/index.tsx';
import type {
  claimTasksTrckMutation,
  claimTasksTrckMutationVariables,
} from './__generated__/index.graphql.ts';
import locked from './assets/locked.webp';
import nbaLocked from './assets/nbaLocked.webp';
import nbaStadiumFire from './assets/nbaStadiumFire.webp';
import stadiumFire from './assets/stadiumFire.webp';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: ${DIAMOND_WIDTH}px;
  height: ${DIAMOND_HEIGHT}px;
`;

const LockedTile = styled.div`
  ${absCenter}
  width: 260%;
  height: 260%;
  background: var(--background-image) no-repeat center / cover;
`;

const fireIconAnimation = keyframes`
  0% {
    transform: translateY(0) translateX(-50%);
  }
  50% {
    transform: translateY(4px) translateX(-50%);
  }
  100% {
    transform: translateY(0) translateX(-50%);
  }
`;
const FireIcon = styled.div`
  width: 260%;
  height: 260%;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 12px;
  background: var(--background-image) no-repeat center / contain;
  animation: ${fireIconAnimation} 3s ease-in-out infinite;
`;

type Props = {
  taskId: string;
  style?: CSSProperties;
  onTrckClaimed: () => Promise<void>;
  disabled?: boolean;
};

const CLAIM_TASKS_TRCK_MUTATION = gql`
  mutation claimTasksTrckMutation($taskId: ID!, $sport: Sport!) {
    claimTask(input: { taskId: $taskId }) {
      currentUser {
        slug
        ...useMaybeClaimInviteTask_currentUser
        ...RefereeRewardDialog_currentUser
      }
      errors {
        message
        code
      }
    }
  }
  ${useMaybeClaimInviteTask.fragments.currentUser}
  ${RefereeRewardDialog.fragments.currentUser}
` as TypedDocumentNode<claimTasksTrckMutation, claimTasksTrckMutationVariables>;

export const NextTrckTile = ({
  taskId,
  style,
  onTrckClaimed,
  disabled,
}: Props) => {
  const { sport } = useSportContext();
  const [claimTasksTrck] = useMutation(CLAIM_TASKS_TRCK_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const maybeClaimInviteTask = useMaybeClaimInviteTask();
  const [overlay, setOverlay] = useState<
    'newChapter' | 'refereeReward' | undefined
  >();
  const { updateDOM } = useTransitionApi();
  const animateSetOverlay = (v: typeof overlay) => {
    updateDOM(() => {
      setOverlay(v);
    });
  };
  const [currentUser, setCurrentUser] =
    useState<
      Nullable<NonNullable<claimTasksTrckMutation['claimTask']>['currentUser']>
    >(null);

  const finishCb = async () => {
    await onTrckClaimed();
    animateSetOverlay(undefined);
  };

  const onClick = async () => {
    animateSetOverlay('newChapter');

    const [claimTaskResult] = await Promise.allSettled([
      claimTasksTrck({
        variables: {
          taskId,
          sport,
        },
      }),
      // Displaying new chapter unlocked screen for at least 3 seconds
      await new Promise(resolve => setTimeout(resolve, 3000)),
    ]);
    const currentUserResult =
      claimTaskResult.status === 'fulfilled'
        ? claimTaskResult.value.data?.claimTask?.currentUser
        : null;
    const claimed = await maybeClaimInviteTask(currentUserResult);
    if (claimed) {
      setCurrentUser(currentUserResult);
      animateSetOverlay('refereeReward');
    } else {
      await finishCb();
    }
  };

  if (disabled) {
    return (
      <Wrapper style={style}>
        <LockedTile
          style={{
            backgroundImage: `url(${sport === Sport.NBA ? nbaLocked : locked})`,
          }}
        />
      </Wrapper>
    );
  }

  return (
    <>
      <Wrapper style={style}>
        <ButtonBase onClick={onClick}>
          <div
            style={{
              viewTransitionName:
                overlay === 'newChapter' ? undefined : VIEW_TRANSITION_NAME,
            }}
          >
            <Tile color={DiamondBaseColor.orange} size={2} borderAnimated />
            <FireIcon
              style={{
                backgroundImage: `url(${sport === Sport.NBA ? nbaStadiumFire : stadiumFire})`,
              }}
            />
          </div>
        </ButtonBase>
      </Wrapper>
      {overlay === 'newChapter' && <NewChapterOverlay />}
      {overlay === 'refereeReward' && (
        <RefereeRewardDialog
          open
          onClose={finishCb}
          currentUser={currentUser}
        />
      )}
    </>
  );
};
